import {dump as dumpYaml, load as loadYaml} from 'js-yaml'
import Papa from 'papaparse'
import {toHtmlTable, toMarkdownTable} from './tableFormatters'

function flattenObject(obj, prefix = '', result = {}) {
    Object.entries(obj).forEach(([key, value]) => {
        const path = prefix ? `${prefix}.${key}` : key
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
            flattenObject(value, path, result)
        } else if (Array.isArray(value)) {
            result[path] = JSON.stringify(value)
        } else {
            result[path] = value
        }
    })
    return result
}

function sanitizeXmlTagName(name) {
    let tag = String(name).trim().replace(/[^A-Za-z0-9_.-]/g, '_')
    if (!tag || /^[0-9.-]/.test(tag)) tag = `_${tag}`
    return tag
}

function escapeXmlText(value) {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;')
}

function valueToXml(key, value, depth, compact) {
    const tag = sanitizeXmlTagName(key)
    const pad = compact ? '' : '  '.repeat(depth)
    const nl = compact ? '' : '\n'

    if (Array.isArray(value)) {
        return value.map(item => valueToXml(tag, item, depth, compact)).join(nl)
    }
    if (value !== null && typeof value === 'object') {
        const inner = Object.entries(value).map(([k, v]) => valueToXml(k, v, depth + 1, compact)).join(nl)
        return `${pad}<${tag}>${nl}${inner}${nl}${pad}</${tag}>`
    }
    if (value === null || value === undefined) {
        return `${pad}<${tag}/>`
    }
    return `${pad}<${tag}>${escapeXmlText(value)}</${tag}>`
}

function buildXmlDocument(data, rootName, itemName, compact = false) {
    let body
    const nl = compact ? '' : '\n'
    if (Array.isArray(data)) {
        body = data.map(item => valueToXml(itemName, item, 1, compact)).join(nl)
    } else if (data !== null && typeof data === 'object') {
        body = Object.entries(data).map(([k, v]) => valueToXml(k, v, 1, compact)).join(nl)
    } else {
        body = valueToXml(itemName, data, 1, compact)
    }
    return `<?xml version="1.0" encoding="UTF-8"?>${nl}<${sanitizeXmlTagName(rootName)}>${nl}${body}${nl}</${sanitizeXmlTagName(rootName)}>`
}

function parseCsv(csvString) {
    const result = Papa.parse(csvString.trim(), {header: true, skipEmptyLines: true, dynamicTyping: true})
    if (result.errors.length) {
        throw new Error(result.errors[0].message)
    }
    return result.data
}

function toCsv(data) {
    const rows = (Array.isArray(data) ? data : [data]).map(row =>
        row !== null && typeof row === 'object' ? flattenObject(row) : {value: row}
    )
    return Papa.unparse(rows)
}

function coerceXmlText(text) {
    if (/^-?\d+(\.\d+)?$/.test(text)) return Number(text)
    if (text === 'true') return true
    if (text === 'false') return false
    return text
}

function xmlElementToValue(element) {
    const children = Array.from(element.children)
    if (children.length === 0) {
        return element.childNodes.length === 0 ? null : coerceXmlText(element.textContent)
    }
    const result = {}
    children.forEach(child => {
        const tag = child.tagName
        const value = xmlElementToValue(child)
        if (Object.prototype.hasOwnProperty.call(result, tag)) {
            result[tag] = Array.isArray(result[tag]) ? [...result[tag], value] : [result[tag], value]
        } else {
            result[tag] = value
        }
    })
    return result
}

function rowsToGrid(rows) {
    if (!rows.length) return []
    const columns = Object.keys(rows[0])
    const cellText = value => (value === null || value === undefined ? '' : String(value))
    return [columns, ...rows.map(row => columns.map(col => cellText(row[col])))]
}

function parseXml(xmlString) {
    const doc = new DOMParser().parseFromString(xmlString.trim(), 'application/xml')
    if (doc.querySelector('parsererror')) {
        throw new Error('Invalid XML')
    }
    return xmlElementToValue(doc.documentElement)
}

export function formatJson(jsonString) {
    return JSON.stringify(JSON.parse(jsonString), null, 2)
}

export function formatYaml(yamlString) {
    return dumpYaml(loadYaml(yamlString))
}

export function jsonToXml(jsonString, options = {}) {
    const {rootName = 'root', itemName = 'item', compact = false} = options
    const data = JSON.parse(jsonString)
    return buildXmlDocument(data, rootName, itemName, compact)
}

export function jsonToYaml(jsonString) {
    return dumpYaml(JSON.parse(jsonString))
}

export function jsonToCsv(jsonString) {
    return toCsv(JSON.parse(jsonString))
}

export function csvToJson(csvString, options = {}) {
    const {compact = false} = options
    return JSON.stringify(parseCsv(csvString), null, compact ? undefined : 2)
}

export function csvToXml(csvString, options = {}) {
    const {compact = false} = options
    return buildXmlDocument(parseCsv(csvString), 'rows', 'row', compact)
}

export function csvToYaml(csvString) {
    return dumpYaml(parseCsv(csvString))
}

export function csvToHtml(csvString) {
    return toHtmlTable(rowsToGrid(parseCsv(csvString)))
}

export function csvToMarkdown(csvString) {
    return toMarkdownTable(rowsToGrid(parseCsv(csvString)))
}

export function yamlToJson(yamlString, options = {}) {
    const {compact = false} = options
    return JSON.stringify(loadYaml(yamlString), null, compact ? undefined : 2)
}

export function yamlToXml(yamlString, options = {}) {
    const {rootName = 'root', itemName = 'item', compact = false} = options
    return buildXmlDocument(loadYaml(yamlString), rootName, itemName, compact)
}

export function yamlToCsv(yamlString) {
    return toCsv(loadYaml(yamlString))
}

export function xmlToJson(xmlString, options = {}) {
    const {compact = false} = options
    return JSON.stringify(parseXml(xmlString), null, compact ? undefined : 2)
}

export function xmlToYaml(xmlString) {
    return dumpYaml(parseXml(xmlString))
}

export function xmlToCsv(xmlString) {
    return toCsv(parseXml(xmlString))
}

// Pretty-prints XML in place (re-indents an existing document) rather than
// round-tripping it through parseXml/buildXmlDocument, which would silently
// drop attributes since that JS-object model has no way to represent them.
function serializeXmlNode(node, depth) {
    const pad = '  '.repeat(depth)
    if (node.nodeType === Node.TEXT_NODE) {
        const text = node.nodeValue.trim()
        return text ? `${pad}${escapeXmlText(text)}` : ''
    }
    if (node.nodeType === Node.COMMENT_NODE) {
        return `${pad}<!--${node.nodeValue}-->`
    }
    const attrs = Array.from(node.attributes || [])
        .map(attr => ` ${attr.name}="${escapeXmlText(attr.value)}"`)
        .join('')
    const childNodes = Array.from(node.childNodes)
    if (childNodes.length === 0) {
        return `${pad}<${node.tagName}${attrs}/>`
    }
    // A single text child (the common leaf case, e.g. <id>4521</id>) stays on one
    // line instead of being pushed onto its own indented line.
    if (childNodes.length === 1 && childNodes[0].nodeType === Node.TEXT_NODE) {
        const text = escapeXmlText(childNodes[0].nodeValue.trim())
        return `${pad}<${node.tagName}${attrs}>${text}</${node.tagName}>`
    }
    const children = childNodes.map(child => serializeXmlNode(child, depth + 1)).filter(Boolean)
    return `${pad}<${node.tagName}${attrs}>\n${children.join('\n')}\n${pad}</${node.tagName}>`
}

export function formatXml(xmlString) {
    const doc = new DOMParser().parseFromString(xmlString.trim(), 'application/xml')
    if (doc.querySelector('parsererror')) {
        throw new Error('Invalid XML')
    }
    return `<?xml version="1.0" encoding="UTF-8"?>\n${serializeXmlNode(doc.documentElement, 0)}`
}
